import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import CertificatePreview from '../components/certificate/CertificatePreview';
import { isPortraitTemplate } from '../components/certificate/templateConfigs';

interface Certificate {
  id: string;
  code: string;
  recipient_name: string;
  recipient_name_english?: string;
  class_name: string;
  issued_at: string;
  is_revoked: boolean;
  class?: {
    id: string;
    title: string;
    offline_hours?: number;
    online_hours?: number;
  };
  provider?: {
    id: string;
    company_name: string;
  };
  template?: {
    id: string;
    name: string;
    template_type?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22;
    border_color: string;
    border_thickness: number;
    logo_url: string | null;
    signature_url: string | null;
    course_name: string;
    signer_name: string;
    signer_title: string;
    signature_url_2?: string | null;
    signer_name_2?: string | null;
  };
}

export default function CertificateViewerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const certificateRef = useRef<HTMLDivElement>(null);

  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      navigate('/');
      return;
    }
    fetchCertificate();
  }, [id, navigate]);

  const fetchCertificate = async () => {
    try {
      // 공개 API 사용 (로그인 불필요)
      const response = await fetch(`/api/v1/certificates/${id}/public`);

      if (!response.ok) {
        throw new Error('인증서를 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setCertificate(data.certificate);
    } catch (error: any) {
      console.error('Failed to fetch certificate:', error);
      alert(error.message || '인증서를 불러오는데 실패했습니다.');
      navigate('/profile/certificates');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\. /g, '.').replace(/\.$/, '');
  };

  // 결제 게이트 임시 중단: PDF 다운로드 대신 브라우저 인쇄(다이얼로그에서 PDF 저장도 가능)로 대체
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">인증서 로딩 중...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-4">인증서를 찾을 수 없습니다.</p>
            <button
              onClick={() => navigate('/profile/certificates')}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              인증서 보관함으로 돌아가기
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // QR 코드 URL - 검증 페이지로 연결
  const qrCodeUrl = `${window.location.origin}/verify/${certificate.code}`;
  const printOrientation = isPortraitTemplate(certificate.template?.template_type || 1)
    ? 'portrait'
    : 'landscape';

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="print:hidden">
        <Header />
      </div>
      <MainTabsNav className="mt-16 print:hidden" />
      <div className="flex-1 pb-8 print:pb-0">
        <div className="max-w-6xl mx-auto px-4 print:px-0 print:h-full print:flex print:flex-col print:items-center print:justify-center">
          {/* Header Actions */}
          <div className="flex justify-between items-center mb-6 print:hidden">
            <button
              onClick={() => navigate('/profile/certificates')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>뒤로 가기</span>
            </button>

            <div className="flex flex-col items-end gap-1">
              {/* Print Button */}
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg transition-colors"
              >
                <Printer className="w-5 h-5" />
                <span>프린트</span>
              </button>
              <span className="text-xs text-gray-400">
                인쇄 대화상자의 "설정 더보기"에서 "머리글과 바닥글"을 꺼주세요.
              </span>
            </div>
          </div>

          {/* Certificate Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 print:hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">수강생</span>
                <p className="font-semibold text-gray-900">{certificate.recipient_name}</p>
              </div>
              <div>
                <span className="text-gray-600">발급 업체</span>
                <p className="font-semibold text-gray-900">
                  {certificate.provider?.company_name || '알 수 없음'}
                </p>
              </div>
              <div>
                <span className="text-gray-600">발급일</span>
                <p className="font-semibold text-gray-900">{formatDate(certificate.issued_at)}</p>
              </div>
              <div>
                <span className="text-gray-600">인증번호</span>
                <p className="font-mono text-xs text-gray-700">{certificate.code}</p>
              </div>
            </div>

            {certificate.is_revoked && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 font-semibold text-sm">
                  ⚠️ 이 인증서는 취소되었습니다.
                </p>
              </div>
            )}
          </div>

          {/* Certificate Preview (화면: 워터마크 미리보기 / PDF: 깨끗하게) */}
          <div className="cert-preview-wrap relative">
            <CertificatePreview
              ref={certificateRef}
              templateType={certificate.template?.template_type || 1}
              borderColor={certificate.template?.border_color || '#1E3A5F'}
              borderThickness={certificate.template?.border_thickness || 4}
              logoUrl={certificate.template?.logo_url || null}
              signatureUrl={certificate.template?.signature_url || null}
              signatureUrl2={certificate.template?.signature_url_2 || null}
              courseName={certificate.template?.course_name || certificate.class_name}
              signerName={certificate.template?.signer_name || certificate.provider?.company_name || ''}
              signerTitle={certificate.template?.signer_title || '담당자'}
              signerName2={certificate.template?.signer_name_2 || null}
              totalHours={(certificate.class?.offline_hours || 0) + (certificate.class?.online_hours || 0)}
              studentName={certificate.recipient_name_english || certificate.recipient_name}
              certificateNumber={certificate.code}
              completionDate={formatDate(certificate.issued_at)}
              qrCodeUrl={qrCodeUrl}
            />
            <div
              className="cert-watermark pointer-events-none absolute inset-0 overflow-hidden select-none"
              aria-hidden="true"
            >
              <div className="absolute inset-[-25%] flex flex-wrap content-around justify-around -rotate-[30deg]">
                {Array.from({ length: 28 }).map((_, i) => (
                  <span
                    key={i}
                    className="text-2xl md:text-3xl font-bold whitespace-nowrap px-6 py-3"
                    style={{ color: 'rgba(30, 58, 95, 0.18)' }}
                  >
                    STAMPFIT • 미리보기 • {certificate.code}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* QR Code Info */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 print:hidden">
            <p className="text-blue-900 text-sm">
              💡 인증서의 QR 코드를 스캔하면 이 인증서의 유효성을 확인할 수 있습니다.
            </p>
          </div>

          {/* @media print: 워터마크 제거 + 인증서 카드만 인쇄, 템플릿 방향에 맞춰 용지 방향 자동 전환 */}
          <style>{`
            @media print {
              @page { size: ${printOrientation}; margin: 0; }
              .cert-watermark { display: none !important; }
              .cert-preview-wrap {
                box-shadow: none !important;
                border: none !important;
                margin: 0 !important;
                width: 100% !important;
              }
              .cert-outer {
                background: transparent !important;
                padding: 0 !important;
                border-radius: 0 !important;
              }
              .cert-card {
                max-width: none !important;
                /* 100%로 딱 맞추면 인쇄 가능 영역 경계와 겹쳐 브라우저가 빈 페이지를 추가하는
                   경우가 있어 약간의 여유(2%)를 둔다 */
                width: 98% !important;
                box-shadow: none !important;
              }
            }
          `}</style>
        </div>
      </div>
      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
}
